/**
 * In-App Purchases (B11 / v1.0.10-b2).
 *
 * Wraps the RevenueCat Capacitor SDK so the rest of the app can call:
 *   - initIAP({ accountId })       on app boot
 *   - getOfferings()               list the Pro products
 *   - purchase(productId)          drive the native purchase sheet
 *   - restorePurchases()           sync prior purchases on a fresh install
 *   - fetchEntitlements()          { pro }
 *
 * Plugin contract: @revenuecat/purchases-capacitor. The plugin is
 * dynamically imported so a missing-package state in dev / web does not
 * brick the bundle. Web builds short-circuit to a "not supported" stub.
 *
 * Source of truth for entitlements is the server (/api/iap/entitlements);
 * this module's getEntitlements() is the client mirror used for paywall
 * gates that need a synchronous answer.
 */
import { getPlatform, type Platform } from "./platform";
import { apiRequestRaw } from "./queryClient";
import { describeDisabledReason, type IapDisabledReason } from "./iapDisabledReason";

// Re-exported so existing callers keep importing the disabled-reason vocab and
// copy from "./iap". The definitions live in ./iapDisabledReason so the pure
// state-machine copy can be unit-tested without the SDK / queryClient.
export { describeDisabledReason, type IapDisabledReason };

// Product IDs MUST match what is registered in App Store Connect / Google
// Play. Renamed in v1.1.0-b2 from the original snake-case scheme to the
// short PascalCase names the user picked in App Store Connect.
//
// 2026-07-15: the Transfer Organiser one-time product was withdrawn — Storage
// Maximisation is part of Pro now. Pro is the only thing we sell, so these are
// the only two products. (The withdrawn SKU also carried a live bug: the client
// spelled it "ND_TransferOrganiser" while the Worker matched
// "NDTransferOrganiser", so its webhook could never have matched. Removing the
// product retires the question.)
export const IAP_PRODUCT_IDS = {
  proMonthly: "NDProMonth",
  proAnnual: "NDProAnnual",
  // Nexus Raid Passport. A separate subscription from Pro — a trainer can hold
  // both — so it has its own products and its own entitlement.
  passportWeekly: "NDPassportWeek",
  passportMonthly: "NDPassportMonth",
} as const;

export type IapProductId = (typeof IAP_PRODUCT_IDS)[keyof typeof IAP_PRODUCT_IDS];

// Pro is the only entitlement. The Transfer Organiser add-on was withdrawn on
// 2026-07-15 (Storage Maximisation is part of Pro) and confirmed unsold, so its
// entitlement is gone rather than grandfathered.
export interface IapEntitlements {
  pro: boolean;
  /** An active Nexus Raid Passport. Independent of `pro`. */
  passport: boolean;
}

export interface IapSubscription {
  tier: "basic" | "pro";
  expiresAt: number | null;
}

export interface IapOffering {
  productId: IapProductId | string;
  priceString: string;       // e.g. "$5.99"
  title: string;             // human label from the store
  description: string;
  period: "weekly" | "monthly" | "annual" | "lifetime" | "unknown";
}

/**
 * Google Play reports subscription product identifiers as
 * `<subscriptionId>:<basePlanId>` (e.g. "ndpromonth:monthly"), whereas App
 * Store Connect uses the bare id. baseProductId strips the base-plan suffix;
 * iOS ids (no colon) pass through unchanged.
 */
function baseProductId(id: string): string {
  return String(id ?? "").split(":")[0] || String(id ?? "");
}

/**
 * Canonical comparison key for a store product id: base subscription id,
 * lowercased. The two stores disagree on BOTH suffix and CASE — App Store
 * Connect allows the PascalCase `NDProMonth`, but Google Play forbids uppercase
 * so the Play subscription is `ndpromonth` (+ a `:basePlanId` suffix). Both must
 * resolve to the same product, so every store-id comparison goes through this
 * — never a raw `===` on the store identifier.
 *
 * Exported because the price lookups (usePrices, the Passport page) must key on
 * the PRODUCT, not on a billing period: Pro monthly and Passport monthly are
 * both "monthly", so anything comparing periods can hand the paywall the wrong
 * subscription's price.
 */
export function productKey(id: string): string {
  return baseProductId(id).toLowerCase();
}

// API key resolution. RevenueCat SDK keys are PER STORE: Apple keys start
// `appl_`, Google keys start `goog_`. An Apple key silently fails
// Purchases.configure() on Android (and vice versa), so we select strictly by
// platform and never cross-fall-back — a wrong-store key is worse than an
// honest `missing_key`, because it fails the same way but hides the cause.
//   iOS/web:  VITE_REVENUECAT_PUBLIC_KEY (canonical since v1.0.10)
//             → VITE_REVENUECAT_IOS_KEY  (alt seen in some env files)
//   Android:  VITE_REVENUECAT_ANDROID_KEY
//             → VITE_REVENUECAT_GOOGLE_KEY (alias)
type RcKeyEnvVar =
  | "VITE_REVENUECAT_PUBLIC_KEY"
  | "VITE_REVENUECAT_IOS_KEY"
  | "VITE_REVENUECAT_ANDROID_KEY"
  | "VITE_REVENUECAT_GOOGLE_KEY"
  | null;

function resolveRevenueCatKey(): { key: string; envVar: RcKeyEnvVar } {
  const env = ((import.meta as any).env ?? {}) as Record<string, string | undefined>;
  const pick = (name: Exclude<RcKeyEnvVar, null>): { key: string; envVar: RcKeyEnvVar } | null => {
    const v = env[name];
    return v ? { key: v, envVar: name } : null;
  };
  const empty = { key: "", envVar: null as RcKeyEnvVar };
  if (getPlatform() === "android") {
    return pick("VITE_REVENUECAT_ANDROID_KEY") || pick("VITE_REVENUECAT_GOOGLE_KEY") || empty;
  }
  // iOS, and web (where the plugin never loads so the value is inert).
  return pick("VITE_REVENUECAT_PUBLIC_KEY") || pick("VITE_REVENUECAT_IOS_KEY") || empty;
}

let { key: REVENUECAT_PUBLIC_KEY, envVar: REVENUECAT_KEY_ENV_VAR } = resolveRevenueCatKey();

/** Tests only: vitest cannot reach the `import.meta.env` this module read at load. */
export function __setRevenueCatKeyForTest(key: string): void {
  REVENUECAT_PUBLIC_KEY = key;
  REVENUECAT_KEY_ENV_VAR = key ? "VITE_REVENUECAT_PUBLIC_KEY" : null;
}

// Runtime handle on the plugin. Held in module scope after a successful
// initIAP() call. Falsy in web / when the package is not installed.
let purchasesModule: any = null;
let lastInitialisedAccountId: number | null = null;
// Promise that resolves once a successful Purchases.configure() returns.
// Anything that needs the SDK ready (getOfferings, purchasePackage) must
// `await ensureConfigured()` first or we hit the
// "Purchases must be configured before calling this function" red banner.
let configurePromise: Promise<boolean> | null = null;
let configured = false;

// Callers that need the SDK BEFORE initIAP has even been called (2026-09-11
// owner screenshot: every price on the paywall read as a placeholder). The
// Why Pro sheet and the Pro locks mount at app start and read prices at once,
// while initIAP waits for the account to load. Their first getOfferings()
// used to answer [] and React Query cached that empty answer, so the paywall
// showed "…" for the rest of the session. Now the read waits here for the
// configure that is on its way.
let configureWaiters: Array<(ok: boolean) => void> = [];
const notifyConfigureWaiters = (ok: boolean) => {
  const waiters = configureWaiters;
  configureWaiters = [];
  for (const w of waiters) w(ok);
};

/**
 * Resolve true once Purchases.configure() has succeeded, false if it failed,
 * was never attempted within `timeoutMs`, or cannot run on this platform.
 * Never throws, never hangs longer than the timeout.
 */
export function whenConfigured(timeoutMs = 20_000): Promise<boolean> {
  if (configured) return Promise.resolve(true);
  if (getPlatform() === "web") return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    let done = false;
    const settle = (ok: boolean) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(ok);
    };
    const timer = setTimeout(() => settle(configured), timeoutMs);
    configureWaiters.push(settle);
    if (configurePromise) configurePromise.then(settle, () => settle(false));
  });
}

// ── Diagnostics state (push9 / v1.4.3-b9) ──────────────────────────────────
// The paywall could silently disable IAP (missing key / plugin / offering)
// with the only signal being a console.warn the trainer never sees. We track
// the last failure reason and the most recent purchase/restore error here so
// the PaywallDiagnosticsPanel can render an honest activation report. We NEVER
// store the API key itself — only whether one was loaded.
// IapDisabledReason is defined in ./iapDisabledReason and re-exported above.

let lastConfigureError: string | null = null;
let lastDisabledReason: IapDisabledReason = null;
let lastPurchaseError: string | null = null;
let lastRestoreError: string | null = null;
let lastRestoreOk: boolean | null = null;

async function loadPurchasesPlugin(): Promise<any | null> {
  if (purchasesModule) return purchasesModule;
  // Web fallback: no native plugin, no IAP. Paywall UI shows a message
  // pointing the trainer at the iOS / Android build.
  if (getPlatform() === "web") return null;
  try {
    // Dynamic import so the bundler does not fail if the package isn't
    // installed yet on the freelancer's machine. See
    // /home/user/workspace/v1_0_10_b2_mac_push_instructions.md for the
    // npm install + pod install sequence.
    purchasesModule = await import("@revenuecat/purchases-capacitor" as any);
    return purchasesModule;
  } catch (err) {
    console.warn("[iap] RevenueCat plugin not installed yet; falling back to stub.", err);
    return null;
  }
}

/**
 * Initialise the RevenueCat SDK with the trainer's account id. Idempotent
 * per account id so re-calls on auth refresh are harmless.
 */
export async function initIAP(opts: { accountId: number }): Promise<void> {
  if (lastInitialisedAccountId === opts.accountId && configured) return;
  configurePromise = (async () => {
    const mod = await loadPurchasesPlugin();
    if (!mod) {
      lastDisabledReason = getPlatform() === "web" ? "web_platform" : "plugin_missing";
      return false;
    }
    if (!REVENUECAT_PUBLIC_KEY) {
      lastDisabledReason = "missing_key";
      console.warn("[iap] VITE_REVENUECAT_PUBLIC_KEY not set; IAP disabled.");
      return false;
    }
    try {
      const Purchases = mod.Purchases ?? mod.default ?? mod;
      await Purchases.configure({
        apiKey: REVENUECAT_PUBLIC_KEY,
        appUserID: String(opts.accountId),
      });
      lastInitialisedAccountId = opts.accountId;
      configured = true;
      lastDisabledReason = null;
      lastConfigureError = null;
      // 2026-10-13: hand RevenueCat the AppsFlyer device id so the RevenueCat ->
      // AppsFlyer integration can report purchases, renewals and trials
      // server-side against the install that produced them. Best effort and
      // off the critical path; a null id (web, attribution off) is skipped.
      void import("./attribution")
        .then(({ getAttributionId }) => getAttributionId())
        .then((id) => (id ? Purchases.setAppsflyerID({ appsflyerID: id }) : undefined))
        .catch(() => undefined);
      return true;
    } catch (err) {
      lastDisabledReason = "configure_failed";
      lastConfigureError = err instanceof Error ? err.message : String(err);
      console.warn("[iap] configure failed", err);
      return false;
    }
  })();
  configurePromise.then(notifyConfigureWaiters, () => notifyConfigureWaiters(false));
  await configurePromise;
}

/**
 * Wait until Purchases.configure() has run successfully. Returns true if
 * the SDK is ready to call getOfferings / purchasePackage, false if IAP
 * is unavailable (web / missing env / no plugin). Used by purchase() and
 * getOfferings() to avoid the "Purchases must be configured before
 * calling this function" red banner the trainer saw on the paywall.
 */
export async function ensureConfigured(): Promise<boolean> {
  if (configured) return true;
  if (configurePromise) return configurePromise;
  return false;
}

/**
 * List the trainer-facing offerings (Pro monthly, Pro annual).
 *
 * App Store Connect / Google Play, surfaced through RevenueCat, is the ONLY
 * source of prices. There is deliberately no hardcoded fallback: this returns
 * an empty list when RevenueCat is unavailable (web, cold start, missing key,
 * plugin not installed), `usePrices` then reports every price as null, and the
 * UI renders PRICE_PLACEHOLDER.
 *
 * A baked-in price is worse than a placeholder. The app previously shipped
 * $12.99/$129.99 fallbacks that were ~2x the real ASC price, and they went
 * unnoticed precisely because a plausible wrong number renders like a right
 * one. If you change a price in ASC, nothing in this repo needs to change.
 */
export async function getOfferings(): Promise<IapOffering[]> {
  const mod = await loadPurchasesPlugin();
  if (!mod) return [];
  // Wait for the configure that the session kicks off once the account is
  // known, rather than answering [] to a paywall that asked a moment early.
  if (!(await whenConfigured())) return [];
  try {
    const Purchases = mod.Purchases ?? mod.default ?? mod;
    const res = await Purchases.getOfferings();
    const current = res?.current ?? res?.offerings?.current ?? null;
    if (!current) return [];

    const packages: any[] = Array.isArray(current.availablePackages)
      ? current.availablePackages
      : [];
    const out: IapOffering[] = [];
    for (const pkg of packages) {
      const product = pkg.product ?? pkg.storeProduct ?? {};
      const id = product.identifier ?? pkg.identifier ?? "";
      out.push({
        productId: id,
        priceString: product.priceString ?? product.price?.toString() ?? "",
        title: product.title ?? "",
        description: product.description ?? "",
        period: detectPeriod(id),
      });
    }
    return out;
  } catch (err) {
    console.warn("[iap] getOfferings failed; prices will render as placeholders", err);
    return [];
  }
}

// Exported for unit tests (Android base-plan suffix handling). Not part of the
// public IAP surface; callers use getOfferings()/usePrices() instead.
export function detectPeriod(productId: string): IapOffering["period"] {
  // productKey() strips the Google Play `:basePlanId` suffix AND lowercases, so
  // Android ("ndpromonth:monthly") maps the same as iOS ("NDProMonth") — without
  // it, Android prices render as placeholders.
  const key = productKey(productId);
  if (key === productKey(IAP_PRODUCT_IDS.proMonthly)) return "monthly";
  if (key === productKey(IAP_PRODUCT_IDS.proAnnual)) return "annual";
  if (key === productKey(IAP_PRODUCT_IDS.passportWeekly)) return "weekly";
  if (key === productKey(IAP_PRODUCT_IDS.passportMonthly)) return "monthly";
  return "unknown";
}

// fallbackOfferings() was removed on 2026-07-15. It hardcoded a "canonical"
// price list that was supposed to mirror App Store Connect and instead drifted
// from it — it shipped $12.99/$129.99 against a real $5.99/$59.99. ASC is the
// only price authority; there is no local copy to drift. See getOfferings().

export interface PurchaseResult {
  ok: boolean;
  productId: string;
  reason?: "userCancelled" | "pluginMissing" | "error";
  message?: string;
}

/**
 * Drive the native purchase sheet. On success the RevenueCat webhook
 * also fires server-side, which is the authoritative entitlement write.
 * We refresh /api/iap/entitlements after the purchase resolves so the UI
 * sees the new state immediately rather than waiting for the webhook.
 */
export async function purchase(productId: IapProductId | string): Promise<PurchaseResult> {
  const mod = await loadPurchasesPlugin();
  if (!mod) {
    return {
      ok: false,
      productId,
      reason: "pluginMissing",
      message: "In-app purchases need the latest iOS or Android build.",
    };
  }
  if (!(await ensureConfigured())) {
    return {
      ok: false,
      productId,
      reason: "error",
      message: "Store not ready yet. Close the paywall and try again in a moment.",
    };
  }
  try {
    const Purchases = mod.Purchases ?? mod.default ?? mod;
    const offerings = await Purchases.getOfferings();
    const current = offerings?.current ?? offerings?.offerings?.current ?? null;
    if (!current) {
      return { ok: false, productId, reason: "error", message: "Could not load store offerings." };
    }
    const pkg = (current.availablePackages ?? []).find((p: any) => {
      const prodId = p.product?.identifier ?? p.storeProduct?.identifier ?? p.identifier;
      // Compare on productKey so the canonical id passed in ("NDProMonth") matches a
      // Google Play package identifier ("ndpromonth:monthly") across suffix+case.
      return productKey(String(prodId ?? "")) === productKey(String(productId));
    });
    if (!pkg) {
      return { ok: false, productId, reason: "error", message: "Product not available on this store." };
    }
    await Purchases.purchasePackage({ aPackage: pkg });
    // 2026-10-13: attribution signal for a completed purchase (no revenue here;
    // see attribution.ts trackSubscription).
    void import("./attribution")
      .then(({ trackSubscription }) => trackSubscription(String(productId)))
      .catch(() => undefined);
    // Trigger a server-side refresh so the SessionProvider mirror catches up.
    await fetchEntitlements().catch(() => null);
    return { ok: true, productId };
  } catch (err: any) {
    if (err?.userCancelled || err?.code === "PURCHASE_CANCELLED" || /cancel/i.test(err?.message ?? "")) {
      return { ok: false, productId, reason: "userCancelled" };
    }
    lastPurchaseError = err?.message ?? "Purchase failed.";
    console.warn("[iap] purchase failed", err);
    return { ok: false, productId, reason: "error", message: err?.message ?? "Purchase failed." };
  }
}

/**
 * The "Restore complete" toast, naming the store account the purchase would
 * live on (2026-09-22: Android read "on this Apple ID").
 */
export function restoreCompleteDescription(platform: "ios" | "android" | "web"): string {
  if (platform === "ios") return "If you had a previous purchase on this Apple ID, it is unlocked now.";
  if (platform === "android") return "If you had a previous purchase on this Google account, it is unlocked now.";
  return "If you previously bought Pro, it is unlocked now.";
}

/**
 * Restore purchases for a reinstall. Drives the native flow then asks
 * the server to re-emit its mirror of the trainer's entitlements.
 */
export async function restorePurchases(): Promise<IapEntitlements | null> {
  lastRestoreError = null;
  const mod = await loadPurchasesPlugin();
  if (mod) {
    try {
      const Purchases = mod.Purchases ?? mod.default ?? mod;
      await Purchases.restorePurchases();
    } catch (err) {
      lastRestoreError = err instanceof Error ? err.message : String(err);
      console.warn("[iap] restorePurchases native flow failed", err);
    }
  }
  try {
    const res = await apiRequestRaw("POST", "/api/iap/restore");
    if (!res.ok) {
      lastRestoreError = `server restore HTTP ${res.status}`;
      lastRestoreOk = false;
      return null;
    }
    const body = (await res.json().catch(() => ({}))) as any;
    lastRestoreOk = true;
    return body?.entitlements ?? null;
  } catch (err) {
    lastRestoreError = err instanceof Error ? err.message : String(err);
    lastRestoreOk = false;
    return null;
  }
}

/**
 * One-shot read of the trainer's entitlements from the server. Source
 * of truth. The client mirror in SessionProvider is the cache.
 */
export async function fetchEntitlements(): Promise<{
  entitlements: IapEntitlements;
  subscription: IapSubscription;
  revenuecatCustomerId: string | null;
} | null> {
  try {
    const res = await apiRequestRaw("GET", "/api/iap/entitlements");
    if (!res.ok) return null;
    const body = (await res.json().catch(() => ({}))) as any;
    return {
      entitlements: {
        pro: !!body?.entitlements?.pro,
        passport: !!body?.entitlements?.passport,
      },
      subscription: {
        tier: body?.subscription?.tier === "pro" ? "pro" : "basic",
        expiresAt: body?.subscription?.expiresAt ?? null,
      },
      revenuecatCustomerId: body?.revenuecatCustomerId ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Ask the RevenueCat SDK directly what this trainer is entitled to.
 *
 * The server mirror (fetchEntitlements) stays the source of truth. This is a
 * belt-and-braces FALLBACK for the case that motivated the whole pro-gating
 * audit: a promotional/comp grant whose webhook never landed leaves the server
 * saying `pro: false` while the RC SDK on the device correctly says Pro. When
 * that happens the trainer is a paying/comped customer being shown an upsell,
 * so we let the SDK win rather than lock them out.
 *
 * Deliberately one-directional: it can only ever GRANT. A `false` here never
 * revokes anything the server granted — on web, without the plugin, or on any
 * SDK error we return null and the caller keeps the server's answer.
 *
 * Tolerant of how the entitlement arrives, because comp grants often carry no
 * product id: we accept either an active entitlement literally identified as
 * "pro", or any active entitlement whose product is a known Pro product.
 */
export async function readNativeEntitlements(): Promise<IapEntitlements | null> {
  const mod = await loadPurchasesPlugin();
  if (!mod) return null;
  if (!(await ensureConfigured())) return null;
  try {
    const Purchases = mod.Purchases ?? mod.default ?? mod;
    const info = await Purchases.getCustomerInfo();
    const active = info?.customerInfo?.entitlements?.active ?? info?.entitlements?.active;
    if (!active || typeof active !== "object") return null;

    const proProductKeys = new Set<string>([
      productKey(IAP_PRODUCT_IDS.proMonthly),
      productKey(IAP_PRODUCT_IDS.proAnnual),
    ]);
    const passportKeys = new Set<string>([
      productKey(IAP_PRODUCT_IDS.passportWeekly),
      productKey(IAP_PRODUCT_IDS.passportMonthly),
    ]);
    let pro = false;
    let passport = false;
    for (const [id, ent] of Object.entries(active as Record<string, any>)) {
      const productId = ent?.productIdentifier ?? ent?.product_identifier ?? "";
      if (id.toLowerCase() === "pro" || proProductKeys.has(productKey(productId))) pro = true;
      if (id.toLowerCase() === "passport" || passportKeys.has(productKey(productId))) {
        passport = true;
      }
    }
    return { pro, passport };
  } catch (err) {
    console.warn("[iap] readNativeEntitlements failed", err);
    return null;
  }
}

/**
 * Synchronous mirror used by SessionProvider. Always returns the safest
 * default (everything locked) when called before initIAP completes.
 */
export function defaultEntitlements(): IapEntitlements {
  return { pro: false, passport: false };
}

// ── Diagnostics (push9 / v1.4.3-b9) ─────────────────────────────────────────

export interface IapDiagnostics {
  /** Where the bundle is running. */
  platform: Platform;
  /** True if a RevenueCat public key was inlined at build time. Never the key itself. */
  keyLoaded: boolean;
  /** Which env var supplied the key (also flags a wrong-store key: an iOS var on Android). */
  keyEnvVar: RcKeyEnvVar;
  /** True if the native @revenuecat/purchases-capacitor plugin loaded. */
  pluginAvailable: boolean;
  /** True once Purchases.configure() resolved successfully. */
  sdkConfigured: boolean;
  /** True if RevenueCat returned a `current` offering with packages. */
  offeringLoaded: boolean;
  /** Product ids found in the current offering (live, from the store). */
  packageIds: string[];
  packageCount: number;
  /** Server-mirrored entitlement state (source of truth). */
  entitlements: IapEntitlements;
  subscriptionTier: IapSubscription["tier"];
  /** Result of the most recent restore, if any. */
  lastRestoreOk: boolean | null;
  lastRestoreError: string | null;
  /** Most recent purchase error (excludes user-cancel). */
  lastPurchaseError: string | null;
  lastConfigureError: string | null;
  /** Why IAP is disabled, or null when it is healthy. */
  disabledReason: IapDisabledReason;
  /** True when purchases cannot run for any reason. */
  iapDisabled: boolean;
  /** One-line human summary for the collapsed diagnostics row. */
  summary: string;
}

/**
 * Snapshot the live IAP / paywall activation state. Safe to call any time:
 * it loads the plugin and probes offerings best-effort and never throws.
 * Used by PaywallDiagnosticsPanel and exposed for support tooling.
 */
export async function getIapDiagnostics(): Promise<IapDiagnostics> {
  const platform = getPlatform();
  // Reflect the platform-aware resolution actually used at configure() time,
  // so the panel shows (e.g.) VITE_REVENUECAT_ANDROID_KEY on an Android build.
  const keyEnvVar: IapDiagnostics["keyEnvVar"] = REVENUECAT_KEY_ENV_VAR;
  const keyLoaded = REVENUECAT_PUBLIC_KEY.length > 0;

  const mod = await loadPurchasesPlugin();
  const pluginAvailable = !!mod;

  // Probe offerings without forcing a configure: getOfferings() already
  // short-circuits to the fallback when not configured, so we read the live
  // packages directly off the SDK when it is ready.
  let packageIds: string[] = [];
  let offeringLoaded = false;
  if (pluginAvailable && configured) {
    try {
      const Purchases = mod.Purchases ?? mod.default ?? mod;
      const res = await Purchases.getOfferings();
      const current = res?.current ?? res?.offerings?.current ?? null;
      const packages: any[] = Array.isArray(current?.availablePackages)
        ? current.availablePackages
        : [];
      packageIds = packages
        .map((p) => p.product?.identifier ?? p.storeProduct?.identifier ?? p.identifier ?? "")
        .filter(Boolean);
      offeringLoaded = packageIds.length > 0;
    } catch (err) {
      lastConfigureError = err instanceof Error ? err.message : String(err);
    }
  }

  // Resolve the disabled reason. When configure never ran (e.g. diagnostics
  // opened before initIAP on web), fall back to the platform/plugin/key chain
  // so the panel is accurate even on a cold open.
  let disabledReason: IapDisabledReason = lastDisabledReason;
  if (disabledReason === null && !configured) {
    if (platform === "web") disabledReason = "web_platform";
    else if (!pluginAvailable) disabledReason = "plugin_missing";
    else if (!keyLoaded) disabledReason = "missing_key";
  }
  const iapDisabled = disabledReason !== null;

  const server = await fetchEntitlements();
  const entitlements = server?.entitlements ?? defaultEntitlements();
  const subscriptionTier = server?.subscription.tier ?? "basic";

  return {
    platform,
    keyLoaded,
    keyEnvVar,
    pluginAvailable,
    sdkConfigured: configured,
    offeringLoaded,
    packageIds,
    packageCount: packageIds.length,
    entitlements,
    subscriptionTier,
    lastRestoreOk,
    lastRestoreError,
    lastPurchaseError,
    lastConfigureError,
    disabledReason,
    iapDisabled,
    summary: iapDisabled
      ? describeDisabledReason(disabledReason)
      : `OK · configured · ${packageIds.length} package(s)`,
  };
}
